import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Page } from '../../components/shell/Page';
import DataGrid, { StatusBadge } from '../../components/datagrid/DataGrid';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  Play,
  ArrowLeft
} from '@phosphor-icons/react';

export default function LearnerExams() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { courseId } = useParams();

  const [exams, setExams] = useState([]);
  const [attempts, setAttempts] = useState({});
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [examsRes, courseRes] = await Promise.all([
        api.get(`/exams?course_id=${courseId}&is_published=true`),
        api.get(`/courses/${courseId}`)
      ]);

      setExams(examsRes.data);
      setCourse(courseRes.data);

      // Fetch attempts for each exam
      const attemptsMap = {};
      for (const exam of examsRes.data) {
        try {
          const attemptsRes = await api.get(`/exams/${exam.id}/attempts`);
          attemptsMap[exam.id] = attemptsRes.data;
        } catch (e) {
          attemptsMap[exam.id] = [];
        }
      }
      setAttempts(attemptsMap);
    } catch (error) {
      toast.error('Failed to load exams');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [courseId]);

  const getCompletedAttempts = (examId) => {
    const examAttempts = attempts[examId] || [];
    return examAttempts.filter(a => a.status === 'graded' || a.status === 'submitted');
  };

  const getMaxAttempts = (exam) => exam.settings?.max_attempts || 1;

  const getAttemptsUsed = (exam) => {
    const examAttempts = attempts[exam.id] || [];
    return examAttempts.filter(a => a.status !== 'in_progress').length;
  };

  const hasInProgress = (examId) => (attempts[examId] || []).some(a => a.status === 'in_progress');

  const getExamStatus = (exam) => {
    if (getCompletedAttempts(exam.id).length > 0) return 'completed';
    if (hasInProgress(exam.id)) return 'in_progress';
    return 'available';
  };

  const attemptsExhausted = (exam) => !hasInProgress(exam.id) && getCompletedAttempts(exam.id).length >= getMaxAttempts(exam);

  const getBestScore = (examId) => {
    const completed = getCompletedAttempts(examId);
    if (completed.length === 0) return null;
    return Math.max(...completed.map(a => a.percentage || 0));
  };

  const startOrResume = (exam) => {
    if (attemptsExhausted(exam)) {
      toast.error('No attempts left for this exam');
      return;
    }
    navigate(`/learn/exams/${exam.id}`);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-8 flex items-center justify-center min-h-[60vh]">
          <div className="animate-pulse">Loading exams...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Page data-testid="learner-exams-page">
        <DataGrid
          title={`Exams — ${course?.title || ''}`}
          rows={exams}
          loading={loading}
          onRefresh={fetchData}
          searchKeys={['description']}
          headerExtra={(
            <Button variant="outline" size="sm" className="h-8" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4" /> Back
            </Button>
          )}
          columns={[
            { key: 'title', header: 'Exam', className: 'font-medium' },
            { key: 'assessment_type', header: 'Type', width: 110, accessor: (e) => e.assessment_type || 'quiz', className: 'capitalize' },
            { key: 'question_count', header: 'Questions', width: 110, align: 'right', accessor: (e) => e.question_count ?? 0 },
            {
              key: 'time_limit', header: 'Time limit', width: 110,
              accessor: (e) => e.settings?.time_limit_minutes ?? null,
              render: (e) => (e.settings?.time_limit_minutes ? `${e.settings.time_limit_minutes} min` : '—'),
            },
            {
              key: 'attempts', header: 'Attempts used', width: 130, align: 'right',
              accessor: (e) => getAttemptsUsed(e),
              render: (e) => `${getAttemptsUsed(e)}/${getMaxAttempts(e)}`,
            },
            {
              key: 'best_score', header: 'Best score', width: 110, align: 'right',
              accessor: (e) => getBestScore(e.id),
              render: (e) => { const s = getBestScore(e.id); return s === null ? '—' : `${s.toFixed(1)}%`; },
            },
            { key: 'status', header: 'Status', width: 120, accessor: (e) => getExamStatus(e), render: (e) => <StatusBadge value={getExamStatus(e)} /> },
          ]}
          rowTone={(e) => {
            const s = getExamStatus(e);
            if (s === 'completed') return 'success';
            if (s === 'in_progress') return 'warning';
            return null;
          }}
          onRowDoubleClick={(e) => startOrResume(e)}
          selectionActions={[
            { key: 'start', label: 'Start / Resume', icon: Play, onClick: ([e]) => startOrResume(e) },
          ]}
          emptyMessage="No exams available. This course doesn't have any published exams yet."
        />
      </Page>
    </DashboardLayout>
  );
}
